import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Loader2, MapPin, User as UserIcon, Plus, Sparkles, ImagePlus, Trash2, Home as HomeIcon } from "lucide-react";
import api, { formatErr, fileUrl } from "../lib/api";
import { useAuth } from "../lib/auth";
import { BRAND_COLORS, STATUS_COLORS, withAlpha } from "../lib/bookingStatus";
import { useLiveStatusSchema } from "../lib/liveStatusSchema";
import BookingProgressTimeline from "../components/BookingProgressTimeline";
import BookingDetailModal from "../components/BookingDetailModal";
import RequestDetailModal from "../components/RequestDetailModal";
import EventChecklistDialog from "../components/dialogs/EventChecklistDialog";
import PropertyStatusEditor from "../components/PropertyStatusEditor";
import PropertyConciergeAssign from "../components/PropertyConciergeAssign";
import AddTileModal from "../components/AddTileModal";
import { AttentionBadge } from "../components/AttentionBadge";
import { CollapsibleSection } from "../components/CollapsibleSection";
import { AbsenceCard } from "../components/customer-detail/AbsenceCard";
import { BookingChecklistPanel } from "../components/customer-detail/BookingChecklistPanel";
import { BookingServicesPanel } from "../components/customer-detail/BookingServicesPanel";
import { useMarkSeen, SeenStatusLine } from "../components/SeenIndicator";
import { PropertyEntriesSection } from "../components/customer-detail/PropertyEntriesSection";
import { TaskRow } from "../components/customer-detail/TaskRow";

/**
 * Feb 2026 · Objekt-Detailseite (Team-Sicht) · User-Directive #303.
 *
 * Nachdem das Kundenprofil auf Header + Präferenzen + Interne Anmerkung
 * verschlankt wurde, leben die Vorbereitungs- und Live-Status-Bereiche hier
 * in einer dedizierten Objekt-Detailansicht. Route: `/team/properties/{id}`.
 *
 * Datenquelle: Wir fragen `/team/customers/{owner_id}/detail` (die
 * bestehende Concierge-View mit Access-Control) und filtern client-seitig
 * auf das ausgewählte Objekt. So bleibt die Backend-Logik (Access-Control,
 * Timeline-Snapshots, Task-Aggregation) unverändert.
 *
 * Bereiche in Reihenfolge (spiegelt die alte Kundenprofil-Reihenfolge):
 *   1. Kompakter Objekt-Header (Foto · Adresse · Concierge · Kunde-Link)
 *   2. Absence-Karte (aktuelle / nächste Abwesenheit DIESES Objekts)
 *   3. Vorbereitungs-Fortschritt (Zeitstrahl)
 *   4. Vorbereitungs-Checkliste
 *   5. Zusatzleistungen
 *   6. Objekt · Live-Status (mit „Kachel hinzufügen" für Admins)
 *   7. Anfragen (Live-Checkliste, gefiltert nach Objekt-ID)
 *   8. Einzelbuchungen dieses Objekts
 */
export default function TeamPropertyDetail() {
  const { id: propertyId } = useParams();
  const nav = useNavigate();
  const { effectiveRole, activeConciergeId } = useAuth();
  const seenNow = useMarkSeen("property", propertyId, true);
  const [data, setData] = useState(null);
  const [ownerId, setOwnerId] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  // Feb 2026 · Objekt-Detail „Alle Einträge" — Klick auf eine
  // Non-Booking-Zeile öffnet je nach Typ RequestDetailModal oder
  // EventChecklistDialog.
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [extrasCatalog, setExtrasCatalog] = useState([]);
  const [addTileOpen, setAddTileOpen] = useState(false);
  const [busyItem, setBusyItem] = useState(null);
  const [uploadingFor, setUploadingFor] = useState(null);
  // Feb 2026 · Objekt-Foto (Property.photo_url) — Admin/Concierge kann das
  // Hero-Bild direkt hier setzen, ohne dass der Kunde durchs Onboarding muss.
  const [photoBusy, setPhotoBusy] = useState(false);
  const photoInputRef = useRef(null);
  const cameraRefs = useRef({});
  const galleryRefs = useRef({});
  const { items: schema, refresh: refreshSchema } = useLiveStatusSchema({ team: true });

  // Deep-link: `/team/properties/{id}#timeline` — pulse the timeline card.
  const location = useLocation();
  const timelineRef = useRef(null);
  const [timelineFlash, setTimelineFlash] = useState(false);

  useEffect(() => {
    api.get("/extras").then(({ data }) => setExtrasCatalog(data || [])).catch(() => {});
  }, []);

  // 1) Owner-ID auflösen — Property in der Team-Objekt-Liste finden.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: props } = await api.get("/team/properties");
        if (cancelled) return;
        const p = (props || []).find((x) => x.id === propertyId);
        if (!p || !p.owner?.id) {
          setNotFound(true);
          return;
        }
        setOwnerId(p.owner.id);
      } catch (err) {
        setNotFound(true);
        toast.error(formatErr(err.response?.data?.detail) || err.message);
      }
    })();
    return () => { cancelled = true; };
  }, [propertyId]);

  // 2) Kunden-Detail laden (Concierge-Access-Control läuft dort bereits).
  const load = useCallback(async () => {
    if (!ownerId) return;
    try {
      const { data } = await api.get(`/team/customers/${ownerId}/detail`);
      setData(data);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
      nav("/team/properties");
    }
  }, [ownerId, nav]);
  useEffect(() => {
    load();
  }, [load]);

  // Timeline-Puls beim Sprung mit `#timeline`.
  useEffect(() => {
    if (location.hash !== "#timeline" || !data || !timelineRef.current) return;
    const el = timelineRef.current;
    const t = setTimeout(() => {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      setTimelineFlash(true);
      setTimeout(() => setTimelineFlash(false), 1600);
    }, 120);
    return () => clearTimeout(t);
  }, [location.hash, data]);

  // Feb 2026 · Objekt-scoped Ableitungen:
  // - property: das ausgewählte Objekt aus data.properties.
  // - Absences: NUR wenn sie zum Objekt gehören.
  // - bookings: gefiltert auf property_id + Concierge-Scope.
  // - tasks: gefiltert auf property_id (Freitext-Anfragen ohne Objekt-
  //   Zuordnung tauchen hier NICHT auf — die gehören ins Kunden-Level und
  //   wurden bewusst dort belassen).
  const propertyData = useMemo(() => {
    if (!data) return null;
    const prop = (data.properties || []).find((p) => p.id === propertyId) || null;
    let bookings = (data.bookings || []).filter((b) => b.property_id === propertyId);
    // Feb 2026 · User-Directive „Objekt-Detail nach Concierge filtern"
    // (1 Sep 2026): Ein Concierge sieht in der Buchungsliste eines
    // Objekts NUR die Buchungen, für die er selbst als Verantwortlicher
    // (`assigned_to`) eingetragen ist. Andere Buchungen desselben Kunden
    // (z.B. Charlotte-zugewiesene an Villa Pfaff) bleiben verborgen.
    const isConciergeScope = effectiveRole === "concierge" && !!activeConciergeId;
    if (isConciergeScope) {
      bookings = bookings.filter((b) => b.assigned_to === activeConciergeId);
    }
    // Feb 2026 · User-Directive „Nächste Abreise korrekt aus zugewiesenen
    // Abwesenheiten lesen" (1 Sep 2026): Für Concierges leiten wir current
    // + next NICHT mehr aus dem globalen Backend-Payload ab (das ist die
    // customer-weite Sicht, ungeachtet von Assignments), sondern aus der
    // bereits gefilterten Booking-Liste. So spiegelt die Absence-Karte
    // genau das, was der Concierge in der Buchungsliste unten sieht.
    let current = null;
    let next = null;
    if (isConciergeScope) {
      const today = new Date().toISOString().slice(0, 10);
      const daysBetween = (a, b) => {
        try { return Math.round((new Date(b) - new Date(a)) / 86400000); }
        catch { return 0; }
      };
      const active = bookings.filter(
        (b) => b.status !== "cancelled"
          && (b.from_date || "") <= today && (b.to_date || "") >= today,
      );
      // Bei mehreren überlappenden nehmen wir die spätestens gestartete —
      // konsistent mit `_current_absence_for_customer` (sort from_date DESC).
      active.sort((a, b) => (b.from_date || "").localeCompare(a.from_date || ""));
      const cur = active[0];
      if (cur) {
        current = {
          ...cur,
          booking_id: cur.id,
          property_name: cur.property_name || prop?.name || "",
          days_remaining: Math.max(daysBetween(today, cur.to_date), 0),
          days_elapsed: Math.max(daysBetween(cur.from_date, today), 0),
        };
      } else {
        const future = bookings
          .filter((b) => b.status !== "cancelled" && (b.from_date || "") > today)
          .sort((a, b) => (a.from_date || "").localeCompare(b.from_date || ""));
        const nx = future[0];
        if (nx) {
          next = {
            ...nx,
            booking_id: nx.id,
            property_name: nx.property_name || prop?.name || "",
            days_until: Math.max(daysBetween(today, nx.from_date), 0),
          };
        }
      }
    } else {
      current = data.current_absence && data.current_absence.property_id === propertyId
        ? data.current_absence : null;
      next = data.next_absence && data.next_absence.property_id === propertyId
        ? data.next_absence : null;
    }
    const filteredItems = (data.tasks?.items || []).filter(
      (it) => (it.property_id || null) === propertyId,
    );
    const done = filteredItems.filter((it) => it.done).length;
    return {
      property: prop,
      current_absence: current,
      next_absence: next,
      bookings,
      tasks: {
        progress: { done, total: filteredItems.length },
        items: filteredItems,
      },
    };
  }, [data, propertyId, effectiveRole, activeConciergeId]);

  const toggleItem = async (item, photoFileId = null) => {
    if (!ownerId) return;
    setBusyItem(item.id);
    try {
      const nextDone = !item.done;
      const { data: fresh } = await api.patch(`/team/customers/${ownerId}/tasks/toggle`, {
        item_id: item.id, done: nextDone, photo_file_id: photoFileId,
      });
      setData(fresh);
      if (nextDone) toast.success("Punkt erledigt · Kalender synchronisiert.");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setBusyItem(null); }
  };

  const onFileChosen = async (e, item) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = "";
    if (!file || !ownerId) return;
    setUploadingFor(item.id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: up } = await api.post("/files/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { data: fresh } = await api.patch(`/team/customers/${ownerId}/tasks/toggle`, {
        item_id: item.id, done: true, photo_file_id: up.id,
      });
      setData(fresh);
      toast.success("Foto gespeichert · Punkt erledigt.");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setUploadingFor(null); }
  };

  // Feb 2026 · Objekt-Foto direkt vom Team pflegen (User-Directive #303 · P1).
  // 1) Datei per `/files/upload` in Object-Storage schieben (bare id zurück)
  // 2) `PATCH /properties/{id}` speichert die id als `photo_url`
  // 3) Kartenansicht + Kunde sehen das neue Foto sofort.
  const onPhotoChosen = async (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Bitte eine Bilddatei wählen."); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("Bild ist zu groß (max. 8 MB)."); return; }
    setPhotoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: up } = await api.post("/files/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { data: fresh } = await api.patch(`/properties/${propertyId}`, { photo_url: up.id });
      setData((d) => ({
        ...d,
        properties: (d?.properties || []).map((x) => x.id === fresh.id ? fresh : x),
      }));
      toast.success("Objekt-Foto aktualisiert.");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setPhotoBusy(false); }
  };

  const onPhotoRemove = async () => {
    setPhotoBusy(true);
    try {
      const { data: fresh } = await api.patch(`/properties/${propertyId}`, { photo_url: "" });
      setData((d) => ({
        ...d,
        properties: (d?.properties || []).map((x) => x.id === fresh.id ? fresh : x),
      }));
      toast.success("Objekt-Foto entfernt.");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setPhotoBusy(false); }
  };

  if (notFound) {
    return (
      <div className="max-w-[1100px] mx-auto px-4 md:px-10 py-10 text-[13px] text-navy-dark/70">
        Objekt nicht gefunden.
        <button
          type="button"
          onClick={() => nav("/team/properties")}
          className="ml-2 text-navy hover:text-gold underline"
        >
          Zurück zur Objekt-Übersicht
        </button>
      </div>
    );
  }

  if (!data || !propertyData || !propertyData.property) {
    return (
      <div
        className="max-w-[1100px] mx-auto px-4 md:px-10 py-10 flex items-center justify-center label-caps"
        data-testid="property-detail-loading"
      >
        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Wird geladen …
      </div>
    );
  }

  const { property, current_absence, next_absence, bookings, tasks } = propertyData;
  const openTasks = tasks.items.filter((i) => !i.done);
  const doneTasks = tasks.items.filter((i) => i.done);
  const owner = data.customer;

  return (
    <div
      className="max-w-[1100px] mx-auto px-4 md:px-10 py-8 md:py-12 pb-24"
      data-testid="property-detail"
    >
      <button
        type="button"
        onClick={() => nav("/team/properties")}
        data-testid="property-detail-back"
        className="inline-flex items-center gap-1.5 text-[11px] tracking-widest uppercase text-navy-dark/60 hover:text-navy mb-4"
      >
        <ChevronLeft className="w-3.5 h-3.5" strokeWidth={1.6} /> Zurück zur Objekt-Übersicht
      </button>

      {/* Kompakter Objekt-Header */}
      <div
        className="mb-6 bg-white border border-navy/10 overflow-hidden"
        data-testid="property-detail-header"
      >
        {/* Feb 2026 · Objekt-Foto (Team-editierbar · User-Directive #303 · P1).
            Klick auf die Fläche öffnet den File-Picker. Kein Kunde-Onboarding
            nötig — Admin/Concierge kann jederzeit ein neues Hero-Foto setzen. */}
        <div className="relative h-48 md:h-56 w-full bg-navy/5 border-b border-navy/10 group">
          {property.photo_url ? (
            <img
              src={fileUrl(property.photo_url)}
              alt={property.name || "Objekt-Foto"}
              className="w-full h-full object-cover"
              loading="lazy"
              data-testid="property-detail-photo"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ) : (
            <div
              className="w-full h-full flex flex-col items-center justify-center gap-2 text-navy-dark/40"
              data-testid="property-detail-photo-empty"
            >
              <HomeIcon className="w-10 h-10" strokeWidth={1.2} />
              <span className="label-caps text-navy-dark/50">Kein Foto hinterlegt</span>
            </div>
          )}
          <input
            ref={photoInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={onPhotoChosen}
            data-testid="property-detail-photo-input"
          />
          <div className="absolute top-3 right-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => photoInputRef.current?.click()}
              disabled={photoBusy}
              data-testid="property-detail-photo-upload-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] tracking-[0.15em] uppercase bg-white/90 backdrop-blur border border-navy/15 hover:border-gold hover:text-gold-dark text-navy transition-colors disabled:opacity-50"
              title="Foto hochladen (max. 8 MB)"
            >
              {photoBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" strokeWidth={1.8} />}
              {property.photo_url ? "Foto ändern" : "Foto hochladen"}
            </button>
            {property.photo_url && (
              <button
                type="button"
                onClick={onPhotoRemove}
                disabled={photoBusy}
                data-testid="property-detail-photo-remove-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] tracking-[0.15em] uppercase bg-white/90 backdrop-blur border border-navy/15 hover:border-red-500 hover:text-red-600 text-navy transition-colors disabled:opacity-50"
                title="Foto entfernen"
              >
                <Trash2 className="w-3.5 h-3.5" strokeWidth={1.8} />
                Entfernen
              </button>
            )}
          </div>
        </div>

        <div className="p-5 md:p-6">
          <div className="label-caps mb-1" style={{ color: BRAND_COLORS.gold }}>Objekt</div>
          <h1 className="font-display text-3xl md:text-4xl text-navy leading-tight" data-testid="property-name">
            {property.name || property.address || "Objekt"}
          </h1>
          <div className="mt-2"><SeenStatusLine entity={{ ...(property || {}), ...(seenNow || {}) }} testid="property-seen-status" /></div>
          <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-[13px] text-navy-dark/75">
            {(property.address || property.city) && (
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5" strokeWidth={1.6} />
                {[property.address, property.city].filter(Boolean).join(", ")}
              </span>
            )}
            {owner?.id && (
              <button
                type="button"
                onClick={() => nav(`/team/customers/${owner.id}`)}
                data-testid="property-detail-owner-link"
                className="inline-flex items-center gap-1.5 hover:text-navy transition-colors"
                title="Zum Kundenprofil"
              >
                <UserIcon className="w-3.5 h-3.5" strokeWidth={1.6} />
                {owner.name || owner.email}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Absence-Karte für dieses Objekt */}
      <div className="mb-6">
        <AbsenceCard current={current_absence} next={next_absence} />
      </div>

      {/* Zeitstrahl */}
      {(current_absence || next_absence) && (
        <div
          id="timeline"
          ref={timelineRef}
          className={`mb-6 transition-shadow duration-700 ${timelineFlash ? "ring-2 ring-gold rounded" : ""}`}
        >
          <BookingProgressTimeline
            booking={current_absence || next_absence}
            property={property}
          />
        </div>
      )}

      {/* Vorbereitungs-Checkliste */}
      {(current_absence || next_absence) && (
        <BookingChecklistPanel
          booking={current_absence || next_absence}
          isCurrent={!!current_absence}
        />
      )}

      {/* Zusatzleistungen */}
      {(current_absence || next_absence) && (
        <BookingServicesPanel
          booking={current_absence || next_absence}
          onChanged={load}
        />
      )}

      {/* Objekt · Live-Status */}
      <div
        className="mb-8"
        data-testid="property-detail-live-status"
      >
        <div className="mb-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="label-caps flex items-center gap-2">Objekt · Live-Status</div>
          {effectiveRole === "admin" && (
            <button
              type="button"
              onClick={() => setAddTileOpen(true)}
              data-testid="live-status-add-tile-btn"
              className="inline-flex items-center gap-1.5 border border-navy/25 hover:border-gold hover:text-gold-dark px-3 py-1.5 text-[11px] tracking-[0.15em] uppercase text-navy bg-white transition-colors"
              title="Neue Kachel systemweit anlegen. Danach pro Kunde als Home-Screen-Kachel aktivierbar."
            >
              <Plus className="w-3.5 h-3.5" strokeWidth={2} />
              Kachel hinzufügen
            </button>
          )}
        </div>
        <div className="space-y-2">
          {effectiveRole === "admin" && ownerId && (
            <PropertyConciergeAssign
              property={property}
              onSaved={(fresh) => setData((d) => ({
                ...d,
                properties: d.properties.map((x) => x.id === fresh.id ? fresh : x),
              }))}
            />
          )}
          {ownerId && (
            <PropertyStatusEditor
              property={property}
              customerId={ownerId}
              schema={schema}
              onSchemaRefresh={refreshSchema}
              onSaved={(fresh) => setData((d) => ({
                ...d,
                properties: d.properties.map((x) => x.id === fresh.id ? fresh : x),
              }))}
            />
          )}
        </div>
      </div>
      {addTileOpen && (
        <AddTileModal
          existingSchema={schema}
          onClose={() => setAddTileOpen(false)}
          onSaved={() => { setAddTileOpen(false); refreshSchema(); }}
        />
      )}

      {/* Anfragen · Live-Checkliste (gefiltert auf dieses Objekt) */}
      <div className="bg-white border border-navy/10 p-5 md:p-6 mb-8" data-testid="property-tasks">
        <CollapsibleSection
          allDone={tasks.progress.total > 0 && tasks.progress.done === tasks.progress.total}
          count={`${tasks.progress.done} / ${tasks.progress.total} erledigt`}
          testid="property-tasks-collapse"
          title={
            <div>
              <div className="label-caps mb-1">Live-Checkliste</div>
              <h2 className="font-display text-xl md:text-2xl text-navy leading-tight inline-flex items-center flex-wrap gap-y-1">
                <span>Anfragen</span>
                <AttentionBadge
                  count={openTasks.length}
                  testid="property-tasks-open-badge"
                  inline
                />
              </h2>
              <p className="text-[12.5px] text-navy-dark/60 mt-1">
                Kunden-Anfragen zu diesem Objekt aus dem „Etwas anfragen"-Formular. Standard-Services und gebuchte Extras erscheinen unter „Zusatzleistungen".
              </p>
            </div>
          }
        >
          {tasks.progress.total > 0 && (
            <div
              className="h-1.5 w-full rounded-full overflow-hidden mb-5"
              style={{ background: withAlpha(BRAND_COLORS.navy, 0.08) }}
            >
              <div
                className="h-full transition-all"
                style={{
                  width: `${Math.round((tasks.progress.done / tasks.progress.total) * 100)}%`,
                  background: tasks.progress.done === tasks.progress.total
                    ? STATUS_COLORS.green
                    : BRAND_COLORS.gold,
                }}
              />
            </div>
          )}
          {tasks.items.length === 0 ? (
            <div className="py-8 text-center" data-testid="property-tasks-empty">
              <Sparkles className="w-6 h-6 mx-auto mb-2" strokeWidth={1.4} style={{ color: BRAND_COLORS.gold }} />
              <div className="font-display text-lg text-navy mb-0.5">Alles ruhig</div>
              <div className="text-[12.5px] text-navy-dark/60">Keine offenen Anfragen zu diesem Objekt.</div>
            </div>
          ) : (
            <>
              {openTasks.length > 0 && (
                <ul className="space-y-2 mb-4" data-testid="property-tasks-open">
                  {openTasks.map((it) => (
                    <TaskRow
                      key={it.id}
                      it={it}
                      busy={busyItem === it.id || uploadingFor === it.id}
                      uploadingFor={uploadingFor}
                      onToggle={toggleItem}
                      onCamera={() => cameraRefs.current[it.id]?.click()}
                      onGallery={() => galleryRefs.current[it.id]?.click()}
                      cameraRef={(el) => { cameraRefs.current[it.id] = el; }}
                      galleryRef={(el) => { galleryRefs.current[it.id] = el; }}
                      onFileChosen={onFileChosen}
                    />
                  ))}
                </ul>
              )}
              {doneTasks.length > 0 && (
                <CollapsibleSection
                  allDone
                  count={String(doneTasks.length)}
                  testid="property-tasks-done-collapse"
                  title={
                    <div className="label-caps" style={{ color: STATUS_COLORS.green }}>
                      Erledigt · {doneTasks.length}
                    </div>
                  }
                >
                  <ul className="space-y-2" data-testid="property-tasks-done">
                    {doneTasks.map((it) => (
                      <TaskRow
                        key={it.id}
                        it={it}
                        busy={busyItem === it.id}
                        uploadingFor={uploadingFor}
                        onToggle={toggleItem}
                        onCamera={() => cameraRefs.current[it.id]?.click()}
                        onGallery={() => galleryRefs.current[it.id]?.click()}
                        cameraRef={(el) => { cameraRefs.current[it.id] = el; }}
                        galleryRef={(el) => { galleryRefs.current[it.id] = el; }}
                        onFileChosen={onFileChosen}
                      />
                    ))}
                  </ul>
                </CollapsibleSection>
              )}
            </>
          )}
        </CollapsibleSection>
      </div>

      {/* Einzelbuchungen dieses Objekts + Extraleistungen + Aufgaben +
          Kalender-Termine — chronologisch. */}
      <PropertyEntriesSection
        propertyId={propertyId}
        bookings={bookings}
        requests={(effectiveRole === "concierge" && activeConciergeId)
          ? (data.service_requests || []).filter((r) => r.assigned_to === activeConciergeId)
          : (data.service_requests || [])}
        events={(effectiveRole === "concierge" && activeConciergeId)
          ? (data.manual_events || []).filter((e) => e.assignee_id === activeConciergeId)
          : (data.manual_events || [])}
        onOpen={(type, row) => {
          if (type === "booking") setSelectedBooking(row);
          else if (type === "request") setSelectedRequest(row);
          else if (type === "event") setSelectedEventId(row.id);
        }}
      />

      {selectedBooking && (
        <BookingDetailModal
          booking={selectedBooking}
          extras={extrasCatalog}
          propertyName={selectedBooking.property_name || property.name || ""}
          mode="team"
          onClose={() => setSelectedBooking(null)}
          onChanged={() => { load().catch(() => {}); }}
        />
      )}
      {selectedRequest && (
        <RequestDetailModal
          request={selectedRequest}
          onClose={() => setSelectedRequest(null)}
          onLinkedChange={() => { setSelectedRequest(null); load().catch(() => {}); }}
        />
      )}
      <EventChecklistDialog
        eventId={selectedEventId}
        open={!!selectedEventId}
        onClose={() => setSelectedEventId(null)}
        onChanged={() => { load().catch(() => {}); }}
      />
    </div>
  );
}
